/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import moment from 'moment';
import { createSelector } from 'reselect';

import { ApiStatus } from '../../../../ts';
import {
  Environment,
  EnvironmentType,
  SkillReference
} from '../../../../ts/assistant';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { SkillType } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { isWxoEnabled } from '../../util/config';
import {
  selectActionSkillId,
  selectDialogSkillId
} from '../assistants/core-selectors';
import {
  isFeatureEnabled,
  selectHideAgentBuilderFeatures
} from '../features/selectors';
import {
  selectAllReleases,
  selectReleaseById
} from '../releases/core-selectors';
import { getAssistantId } from '../session/selectors';
import { selectSkillById } from '../skills/core-selectors';
import { entityAdapter } from './adapter';

const selectors = entityAdapter.getSelectors(
  (state: RootState) => state.environments
);

export const selectListEnvironmentsStatus = (state: RootState): ApiStatus =>
  state.environments.listStatus;

const selectAllEnvInternal = (state: RootState): Environment[] =>
  state.environments ? selectors.selectAll(state) : [];

/**
 * Returns all environments, sorted so draft is the first in the array,
 * live is last in the array, and between are all others sorted oldest first
 */
export const selectAllEnvironments = createSelector(
  [selectAllEnvInternal],
  (environments): Environment[] => {
    // first sort by creation time, oldest first
    environments.sort((a, b) =>
      moment.utc(a.created).diff(moment.utc(b.created))
    );
    // find and move draft to first element
    const draftIndex = environments.findIndex(
      env => env.environment === EnvironmentType.DRAFT
    );
    if (draftIndex > 0) {
      environments.splice(0, 0, environments.splice(draftIndex, 1)[0]);
    }
    // find and move live to last element
    const liveIndex = environments.findIndex(
      env => env.environment === EnvironmentType.LIVE
    );
    if (liveIndex > -1) {
      environments.splice(
        environments.length - 1,
        0,
        environments.splice(liveIndex, 1)[0]
      );
    }
    return environments;
  }
);

/**
 * Returns the array of environments but after removing the Draft and Live
 * instances -- will be [] for an assistant with no custom environments
 */
export const selectCustomEnvironments = createSelector(
  [selectAllEnvInternal],
  (environments): Environment[] => {
    return environments.filter(
      el =>
        el.environment !== EnvironmentType.LIVE &&
        el.environment !== EnvironmentType.DRAFT
    );
  }
);

export const selectEnvironmentCount = (state: RootState): number =>
  state.environments ? selectors.selectTotal(state) : 0;

export const selectEnvironmentIds = selectors.selectIds;

export const selectEnvironmentById = (
  state: RootState,
  id: string
): Environment | null =>
  state.environments ? selectors.selectById(state, id) : null;

export const areEnvironmentsLoading = createSelector(
  [selectListEnvironmentsStatus],
  (listEnvironmentsStatus): boolean =>
    listEnvironmentsStatus === ApiStatus.LOADING
);

export const selectSkillReferencesForEnvironment = (
  state: RootState,
  environmentId: string
): SkillReference[] => {
  const environment = selectEnvironmentById(state, environmentId);
  return environment?.skill_references || [];
};

export const selectDialogSkillIdFromEnvironment = (
  state: RootState,
  environmentId: string
): string | undefined => {
  const reference = selectSkillReferencesForEnvironment(
    state,
    environmentId
  ).find(skill => skill.type === SkillType.DIALOG);
  return reference?.skill_id ?? undefined;
};

/**
 * Returns the draft environment.
 */
export const selectDraftEnvironment = createSelector(
  [selectAllEnvironments],
  (environments): Environment | undefined => {
    return environments.find(e => e.environment === EnvironmentType.DRAFT);
  }
);

export const selectDraftEnvironmentId = createSelector(
  [selectDraftEnvironment],
  (environment): string | undefined => environment?.environment_id
);

/**
 * Returns the Live environment.
 *
 * @param {object} state The redux state
 * @returns {object} The target environment which name matches `Live`
 */
export const selectTargetEnvironment = createSelector(
  [selectAllEnvironments],
  (environments): Environment | undefined =>
    environments?.find(e => e.environment === EnvironmentType.LIVE)
);

/**
 * Returns the id of the Live environment.
 *
 * @param {object} state The redux state
 * @returns {string} The target environment id which name matches `Live`
 */
export const selectTargetEnvironmentId = createSelector(
  [selectTargetEnvironment],
  (environment): string | undefined => environment?.environment_id
);

/**
 * Decide whether the user can enable Classic IA while operating under WXO.
 * Note that Classic IA is not supported if we are hiding Agent Builder
 * features.
 */
export const selectIsClassicWxoSupported = (state: RootState): boolean =>
  isWxoEnabled &&
  isFeatureEnabled(state, FeatureFlag.IA_WXO_CLASSIC_SUPPORTED) &&
  !selectHideAgentBuilderFeatures(state);

/**
 * Decide whether the user can toggle Dialog. Dialog is supported in stand-alone
 * WXA, and under WXO only if dialog support has been explicitly enabled.
 * Note that toggling dialog is not supported if we are hiding Agent Builder
 * features.
 */
export const selectIsDialogSupported = createSelector(
  [
    (state: RootState) =>
      isFeatureEnabled(state, FeatureFlag.IA_WXO_DIALOG_SUPPORTED),
    selectHideAgentBuilderFeatures
  ],
  (isDialogEnabledInWxo, shouldHideAgentBuilderFeatures) =>
    !isWxoEnabled || (isDialogEnabledInWxo && !shouldHideAgentBuilderFeatures)
);

/**
 * Utility function that determines whether a dialog skill is enabled in
 * the specified environment. It also takes into account whether the Dialog
 * feature is supported in the first place in the current platform.
 *
 * @param environment The environment in which we will try to find an enabled
 * dialog skill
 * @param isDialogSupported Whether the dialog feature is supported at all in
 * the current deployment
 * @returns true if an enabled dialog skill can be found in the specified
 * environment and the dialog feature is supported
 */
const selectIsDialogEnabled = (
  environment: Environment,
  isDialogSupported: boolean
): boolean => {
  if (isDialogSupported) {
    const skill = environment?.skill_references?.find(
      skill => skill.type === SkillType.DIALOG
    );
    // Also need to explicitly check if dialogSkill is defined because if it isn't,
    // technically dialog is "disabled" and shouldn't show
    return Boolean(skill && !skill?.disabled);
  } else {
    return false;
  }
};

/**
 * Return true if the dialog skill is enabled in the target/live environment
 */
export const selectIsDialogEnabledInTargetEnvironment = createSelector(
  [selectTargetEnvironment, selectIsDialogSupported],
  selectIsDialogEnabled
);

export const selectIsDialogEnabledInDraftEnvironment = createSelector(
  [selectDraftEnvironment, selectIsDialogSupported],
  selectIsDialogEnabled
);
export const selectIsDialogEnabledForEnvironment = createSelector(
  [selectEnvironmentById, selectIsDialogSupported],
  selectIsDialogEnabled
);

export const selectSearchSkillIdByEnvironment = (
  state: RootState,
  environmentId: string
): string => {
  const environment = selectEnvironmentById(state, environmentId);
  const skill = environment?.skill_references?.find(
    sr => sr.type === SkillType.SEARCH
  );
  return skill?.skill_id;
};

export const selectEnvironmentIndexById = createSelector(
  [
    selectAllEnvironments,
    (__: RootState, environmentId: string): string => environmentId
  ],
  (allEnvironments, environmentId): number =>
    allEnvironments.findIndex(entry => entry.environment_id === environmentId)
);

export const selectIsSkillEnabledInEnvironment = (
  state: RootState,
  environmentId?: string,
  skillId?: string
): boolean => {
  const env = selectEnvironmentById(state, environmentId);
  if (!env) {
    return false;
  }
  const skillReference = env.skill_references?.find(
    ref => ref.skill_id === skillId
  );
  // NOTE if skillReference exists but skillReference.disabled == undefined, then it is old version and is enabled
  return Boolean(skillReference && !skillReference.disabled);
};

const selectIsSkillEnabledInAnyEnvironment = (
  state: RootState,
  type: SkillType
) => {
  const enabledEnv = selectAllEnvironments(state).find(env => {
    const skillReference = env.skill_references?.find(ref => ref.type === type);
    // NOTE if skillReference exists but skillReference.disabled == undefined, then it is old version and is enabled
    return skillReference && !skillReference.disabled;
  });
  return Boolean(enabledEnv);
};

export const selectIsSkillAutocreated = (
  state: RootState,
  environmentId: string,
  skillId: string
): boolean => {
  const env = selectEnvironmentById(state, environmentId);
  if (env) {
    const skillReference = env.skill_references?.find(
      ref => ref.skill_id === skillId
    );
    return skillReference?.disabled !== undefined;
  }
  return false;
};

export const selectIsSearchEnabledInAnyEnvironment = (
  state: RootState
): boolean => selectIsSkillEnabledInAnyEnvironment(state, SkillType.SEARCH);

/**
 * Returns true if the content (action or action and dialog) or assistant are read only.
 */
export const selectDraftLastEdited = (state: RootState): moment.Moment => {
  const dialogSkillId = selectDialogSkillId(state);
  const actionSkillId = selectActionSkillId(state);

  const actionSkill = selectSkillById(state, actionSkillId);
  const dialogSkill = selectSkillById(state, dialogSkillId);

  return moment.max([
    moment(actionSkill?.updated),
    moment(dialogSkill?.updated)
  ]);
};

type EnvironmentWithRelease = Environment & { release?: string };
/**
 * Returns the environments along with release assigned to each one of them.
 */
export const selectEnvironmentsAndRelease = createSelector(
  [selectAllEnvironments, selectAllReleases],
  (environments, releases): EnvironmentWithRelease[] => {
    return environments.map(environment => {
      const release = releases.find(release =>
        release.linked_assistant_references?.some(
          ref =>
            ref.assistant_id === environment.environment_id ||
            ref.environment_id === environment.environment_id
        )
      );

      return {
        ...environment,
        release: release?.release
      };
    });
  }
);

/**
 * Returns the environments along with release assigned to each one of them.
 */
export const selectEnvironmentsByRelease = createSelector(
  [selectAllEnvironments, selectReleaseById],
  (environments, release): Environment[] => {
    return environments.filter(environment =>
      release?.linked_assistant_references?.some(
        ref =>
          ref.assistant_id === environment.environment_id ||
          ref.environment_id === environment.environment_id
      )
    );
  }
);

/**
 * Fetches selectEnvironmentsByRelease and processes the result array
 * so that 'live' is the first element
 */
export const selectOrderedEnvironmentsByRelease = createSelector(
  [selectEnvironmentsByRelease],
  (environments): Environment[] => {
    if (environments.length > 1) {
      // reorder the array so Live is the first tag if present
      const liveIndex = environments.findIndex(env => env.name === 'live');
      // note -1 not found, 0 already first
      if (liveIndex > 0) {
        let displayOrderEnvironments = [environments[liveIndex]];
        displayOrderEnvironments = displayOrderEnvironments.concat(
          environments.slice(0, liveIndex)
        );
        if (liveIndex < environments.length - 1) {
          displayOrderEnvironments = displayOrderEnvironments.concat(
            environments.slice(liveIndex + 1, environments.length - 1)
          );
        }
        return displayOrderEnvironments;
      } else {
        return environments;
      }
    } else {
      return environments;
    }
  }
);

/**
 * Returns true if there are cached environments or if the list environment
 * request finished. We show a loading spinner if there are no cache results
 */
export const isEnvironmentLoadingComplete = createSelector(
  [
    state => getAssistantId(state),
    selectAllEnvInternal,
    selectListEnvironmentsStatus
  ],
  (assistantId, environments, listEnvironmentsStatus): boolean => {
    if (assistantId && environments[0]?.assistant_id === assistantId) {
      return true;
    }

    return (
      listEnvironmentsStatus === ApiStatus.SUCCEEDED ||
      listEnvironmentsStatus === ApiStatus.ERROR
    );
  }
);

export const selectReleaseByEnvironment = (
  state: RootState,
  environmentId: string
): string => {
  return selectEnvironmentsAndRelease(state).find(
    e => e.environment_id === environmentId
  )?.release;
};

export const selectIsCreatingEnvironment = (state: RootState) =>
  state.environments.createStatus === ApiStatus.LOADING;

export const selectIsUpdatingEnvironment = (state: RootState) =>
  state.environments.updateStatus === ApiStatus.LOADING;

export const selectIsDeletingEnvironment = (state: RootState) =>
  state.environments.deleteStatus === ApiStatus.LOADING;
